import { useRef, useState } from "react";
import {
  ArtButton,
  ArtDialog,
  ArtTable,
  ArtTableHandle,
  confirmDialog,
  HeadCell,
  SSRTableBuilder,
  useApiService,
} from "@art/shared-ui";
import { faEdit, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import ArtEnvironmentAddPage from "./add-dialog.component";
import { usePageWrapper } from "../../../hooks";
import { Box } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import "../application-users/user.css"
import { useAttributePermission } from "../../permissions";
import { tableParamNav } from "../../../utils/table-utils";


function ArtEnvironmentSettingsPage() {
  const [queryParams, setQueryParams]: any = useSearchParams();

  const tableRef = useRef<ArtTableHandle>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selectedData, setSelectedData]: [Data | null, any] = useState(null);
  const apiService = useApiService();

  const canCreateEnvironments = useAttributePermission("PLATFORM_SETTINGS", "ENVIRONMENT_MANAGEMENT", "CreateEnvironments");
  const canEditEnvironments = useAttributePermission("PLATFORM_SETTINGS", "ENVIRONMENT_MANAGEMENT", "EditEnvironments");
  const canDeleteEnvironments = useAttributePermission("PLATFORM_SETTINGS", "ENVIRONMENT_MANAGEMENT", "DeleteEnvironments");

  interface Data {
    id: string;
    title: string;
    tenant_name: string;
  }

  const headCells: HeadCell<Data>[] = [
    {
      id: "title",
      numeric: false,
      disablePadding: false,
      label: "Title",
      getColData: (row) => row["title"],
    },
    {
      id: "tenant_name",
      numeric: false,
      disablePadding: false,
      label: "Tenant Name",
      getColData: (row) => row.tenant_name,
    },
  ];

  const tableBuilder = SSRTableBuilder<Data>({
    endpoint: "/environments",
    method: "get",
    apiService: apiService,

    onFetch: (params) => {
      let currentPage = params["page"] || "1";
      let searchText = params["search_text"] || ""; // search

      //page and search added to params
      const newParams: any = {
        page: currentPage,
      };
      if (searchText) {
        newParams.search = searchText;
      }
      setQueryParams(newParams, tableParamNav(queryParams, "page", currentPage)); // Update this

      return {
        page: currentPage,
        search_text: searchText,
      };
    },
    onResponse: (data) => {
      return {
        data: data.data.environments ?? [],
        total: data.data.total_count,
      };
    },
  });

  const openAddEnvironmentDialog = (data?: Data) => {
    setSelectedData(data);
    setAddDialogOpen(true);
  };
  const closeAddEnvironmentDialog = () => {
    setSelectedData(null);
    setAddDialogOpen(false);
  };

  const onSave = () => {
    closeAddEnvironmentDialog();
    tableRef.current?.refresh(0);
  };

  return (
    <>
      <ArtDialog
        title={selectedData ? "Edit Environment" : "Add Environment"}
        open={addDialogOpen}
        onClose={closeAddEnvironmentDialog}
        type="form"
        actionBuilder={(props) => {
          return (
            <Box sx={{ display: "flex", gap: "15px" }}>
              <ArtButton
                buttonType="button"
                dataQa="btn-save"
                label={selectedData ? "Update" : "Add"}
                onClick={() => props.submit?.()}
              />
              <ArtButton
                buttonType="button"
                dataQa="btn-reset"
                label="Reset"
                variant="outlined"
                color="error"
                onClick={() => props.reset?.()}
              />
            </Box>
          );
        }}
      >
        <ArtEnvironmentAddPage data={selectedData} onSave={onSave} />
      </ArtDialog>
      <Box className="non-clickable-table">
        <ArtTable
          ref={tableRef}
          headCells={headCells}
          builder={tableBuilder}
          search={{
            enabled: true,
            value: queryParams.get("search") || "", // check and add the search text from params
          }}
          pagination={{
            initialPage: parseInt(queryParams.get("page") || "1") - 1,
          }}
          toolbarActions={canCreateEnvironments ? [
            <ArtButton
              variant="contained"
              dataQa="btn-add"
              label="Add"
              icon={faPlus}
              onClick={() => {
                openAddEnvironmentDialog();
              }}
            />,
          ] : []}
          rowActions={[
            ...(canEditEnvironments ? [{
              icon: faEdit,
              label: "Edit",
              onClick: (row: Data) => {
                openAddEnvironmentDialog(row);
              },
            }] : []),
            ...(canDeleteEnvironments ? [{
              icon: faTrash,
              label: "Delete",
              onClick: (row: Data) => {
                confirmDialog("confirm", "Do you want to delete?").then(() => {
                  apiService.deleteData(`/environments/${row.id}`, {
                    onSuccess: () => {
                      onSave();
                    },
                    onError: (err: any) => {
                      console.log(err);
                    },
                  })();
                });
              },
            }] : []),
          ]}
        />
      </Box>

    </>
  );
}

export default usePageWrapper(ArtEnvironmentSettingsPage, {
  page_title: "Environment",
});
